import { useState } from "react";
import { createFileRoute, Outlet, useMatches, useNavigate, redirect } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/dashboard-layout";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { queuesQueryOptions, deleteQueueFn } from "@/server/queues";
import { toast } from "sonner";
import { checkIfUserMayWrite, checkIfUserMayDelete, getPermissionRestraintMessage } from "@/lib/utils";
import { usePermissionRedirect } from "@/hooks/use-permission-redirect";

export const Route = createFileRoute("/_dashboard/queues")({
  component: QueueRouteComponent,
  loader: ({ context }) => {
    return {
      crumb: "Queues",
      userProfileId: (context as any).user?.profile,
      accessControl: (context as any).user?.accessControl,
      user: (context as any).user,
    };
  },
});

function QueueRouteComponent() {
  const queuesQuery = useQuery(queuesQueryOptions);
  const queues = queuesQuery.data ?? [];
  const navigate = useNavigate();
  const { userProfileId, accessControl, user } = Route.useLoaderData();
  usePermissionRedirect({ user, permission: "QUEUE" });
  //If the user has no profile, it means they have access to everything. Profiles are restrictive only.
  const mayWrite = checkIfUserMayWrite(userProfileId, accessControl, "QUEUE");
  const mayDelete = checkIfUserMayDelete(userProfileId, accessControl, "QUEUE");
  const queryClient = useQueryClient();
  const matches = useMatches();
  const isRootQueues = matches[matches.length - 1].fullPath === "/queues";

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState<{ id: number; name: string } | null>(null);
  const [loading, setLoading] = useState(false);

  function handleDeleteClick(queueId: number, queueName: string) {
    setSelectedQueue({ id: queueId, name: queueName });
    setDialogOpen(true);
  }

  const deleteQueueMutation = useMutation({
    mutationFn: deleteQueueFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["queues"] });
      queryClient.invalidateQueries({ queryKey: ["queue-group-queues"] });
      queuesQuery.refetch();
      setDialogOpen(false);
      setSelectedQueue(null);
      setLoading(false);
      navigate({ to: "/queues" });
    },
  });

  async function handleConfirmDelete() {
    if (!selectedQueue) return;
    setLoading(true);
    const deleteQueuePromise = deleteQueueMutation.mutateAsync({
      data: { queueId: Number(selectedQueue.id) },
    });
    toast.promise(deleteQueuePromise, {
      loading: "Deleting queue...",
      success: () => {
        return `Queue "${selectedQueue.name}" has been deleted successfully`;
      },
      error: (error) => error.message || "Failed to delete queue",
    });
  }

  return (
    <>
      <DashboardLayout
        submenu={{
          items: queues,
          isLoading: queuesQuery.isLoading,
          onClick: (item: any, functionType: string, parent?: { name: string; id: string }) => {
            if (functionType === "delete") {
              if (!mayDelete) {
                toast.error(getPermissionRestraintMessage("delete queues"));
                return;
              }
              handleDeleteClick(item.parent.id, item.parent.name);
            }
          },
          title: "Queues",
          active: true,
          createNew: mayWrite ? [{ label: "+ Create new queue", href: "/queues/new-queue" }] : undefined,
        }}
      >
        {isRootQueues ? (
          <Card>
            <CardContent>
              <p className="text-md pb-20 pt-15 text-center">
                Please select a Queue from the submenu to view its details or manage it.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Outlet />
        )}
      </DashboardLayout>
      <ConfirmDialog
        open={dialogOpen}
        title="Delete queue"
        description={selectedQueue ? `Are you sure you want to delete the queue "${selectedQueue.name}"?` : ""}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDialogOpen(false);
          setSelectedQueue(null);
        }}
        loading={loading}
      />
    </>
  );
}
